import { Outlet, useNavigate, NavLink } from 'react-router-dom';
import { Topbar } from '@/components/ui/Topbar';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  User,
  Calendar,
  LogOut,
  Menu,
  X,
  Wallet,
  CreditCard,
  Users,
  List,
  History,
  Settings,
  FileText,
  Award,
  TestTube,
  Radio,
  Loader2,
  AlertTriangle,
  Lock,
  Clock,
  CheckCircle2,
  Sparkles,
  RefreshCw,
  Ban,
} from 'lucide-react';
import { cn, Button } from '@medikei/ui';
import { useDoctorExpressSocket, InvitationData } from '@/hooks/express/useDoctorExpressSocket';
import { IncomingCallModal, IncomingInvitationData } from '@/components/doctor/IncomingCallModal';
import { DoctorOnboardingModal } from '@/components/doctor/DoctorOnboardingModal';
import { DoctorProfilePreviewModal } from '@/components/doctor/DoctorProfilePreviewModal';
import { FloatingHelpButton } from '@/landing/v3/sections/FloatingHelpButton';
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import { GlobalSalesBanner } from '@/components/common/GlobalSalesBanner';
import { useDoctorProfile } from '@/hooks/doctor/useDoctorProfile';
import { useAuth } from '@/contexts/AuthContext';
import { useDoctorQueueStore } from '@/stores/useDoctorQueueStore';

const doctorNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/medico/dashboard' },
  { icon: User, label: 'Meu Perfil', path: '/medico/perfil' },
  { icon: List, label: 'Fila', path: '/medico/fila' },
  { icon: Users, label: 'Pacientes', path: '/medico/pacientes' },
  { icon: Calendar, label: 'Agenda', path: '/medico/agenda' },
  { icon: FileText, label: 'Receitas', path: '/medico/receitas' },
  { icon: Award, label: 'Atestados', path: '/medico/atestados' },
  { icon: TestTube, label: 'Exames', path: '/medico/exames' },
  { icon: History, label: 'Histórico', path: '/medico/historico' },
  { icon: CreditCard, label: 'Pagamentos', path: '/medico/pagamentos' },
  { icon: Wallet, label: 'Carteira', path: '/medico/carteira' },
  { icon: Settings, label: 'Configurações', path: '/medico/configuracoes' },
];

interface DoctorSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  isBlocked?: boolean;
}

function DoctorSidebar({ collapsed, onToggle, mobileOpen, onCloseMobile, isBlocked }: DoctorSidebarProps) {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const allowedNavItems = isBlocked
    ? doctorNavItems.filter((item) => item.path === '/medico/carteira' || item.path === '/medico/pagamentos')
    : doctorNavItems;

  const handleLogout = async () => {
    onCloseMobile();
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  return (
    <>
      {/* Mobile Backdrop (< 640px) */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-xs sm:hidden transition-opacity"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'bg-card border-r border-border transition-all duration-300 flex flex-col',
          // Mobile Drawer: fixed on small screens, in-flow on >= sm
          'fixed inset-y-0 left-0 z-50 sm:static sm:z-auto',
          mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full sm:translate-x-0',
          collapsed ? 'sm:w-16' : 'sm:w-64',
          'w-64'
        )}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          {(!collapsed || mobileOpen) && (
            <img src="/logo.png" alt="Medikei" className="h-8 w-auto" decoding="async" />
          )}
          {/* Desktop Toggle Button */}
          <button
            onClick={onToggle}
            className="hidden sm:flex p-2 rounded-lg hover:bg-muted/80 text-foreground transition-colors"
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {collapsed ? <Menu size={20} /> : <X size={20} />}
          </button>
          {/* Mobile Close Button */}
          <button
            onClick={onCloseMobile}
            className="sm:hidden p-2 rounded-lg hover:bg-muted/80 text-foreground transition-colors"
            aria-label="Fechar menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {allowedNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onCloseMobile}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                  'hover:bg-muted/80 text-foreground',
                  isActive && 'bg-primary/10 text-primary font-semibold shadow-xs'
                )
              }
            >
              <item.icon size={20} aria-hidden="true" />
              {(!collapsed || mobileOpen) && (
                <span className="text-sm font-medium">{item.label}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-border">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-destructive hover:bg-destructive/10 transition-colors w-full"
          >
            <LogOut size={20} aria-hidden="true" />
            {(!collapsed || mobileOpen) && (
              <span className="text-sm font-medium">Sair</span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}

export function DoctorLayout() {
  const { user } = useAuth();
  const { data: profile, refetch: refetchProfile } = useDoctorProfile(user?.id || '');
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [onboardingInitialStep, setOnboardingInitialStep] = useState(1);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  const {
    isOnline,
    setIsOnline,
    currentInvitation,
    setCurrentInvitation,
    fetchStatus,
    toggleOnline,
  } = useDoctorQueueStore();

  const token = localStorage.getItem('accessToken') || '';
  const doctorStatus = profile?.status || (user as any)?.doctorStatus || 'PENDING';
  const isApproved = doctorStatus === 'APPROVED';
  const isRejected = doctorStatus === 'REJECTED';
  const isBlocked = doctorStatus === 'BLOCKED' || (user as any)?.status === 'BLOCKED';
  const blockedReason = (profile as any)?.blockedReason || (user as any)?.blockedReason || null;

  // Doctor socket for real-time consultation invitations
  const { onNewInvitation, onInvitationCancelled } = useDoctorExpressSocket({
    token,
    enabled: isOnline,
  });

  // Carrega status inicial do médico ao montar ou aprovar
  useEffect(() => {
    if (!isApproved) {
      setIsOnline(false);
      return;
    }
    fetchStatus();

    // Se aprovado pela primeira vez, convida para concluir a Fase 2 do onboarding (público atendido, modalidades e tutorial)
    if (profile?.id) {
      const tutorialKey = `medikei_doctor_tutorial_done_${profile.id}`;
      const tutorialDone = localStorage.getItem(tutorialKey);
      if (!tutorialDone) {
        setOnboardingInitialStep(5);
        setIsOnboardingOpen(true);
        localStorage.setItem(tutorialKey, 'true');
      }
    }
  }, [isApproved, fetchStatus, setIsOnline, profile?.id]);

  // Listen to incoming consultation invitations
  useEffect(() => {
    const unsubNew = onNewInvitation((inv: InvitationData) => {
      console.info('[DOCTOR-LAYOUT] 🔴 Convite recebido via Socket:', inv);
      setCurrentInvitation(inv as IncomingInvitationData);
    });

    const unsubCancel = onInvitationCancelled(() => {
      console.info('[DOCTOR-LAYOUT] ⚪ Convite cancelado ou expirado');
      setCurrentInvitation(null);
    });

    return () => {
      unsubNew();
      unsubCancel();
    };
  }, [onNewInvitation, onInvitationCancelled, setCurrentInvitation]);

  const handleToggleOnline = async () => {
    if (!isApproved) {
      setIsOnboardingOpen(true);
      return;
    }
    setIsTogglingStatus(true);
    try {
      await toggleOnline();
    } catch (err) {
      console.error('Failed to toggle doctor status:', err);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleToggleSidebar = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 640) {
      setMobileOpen((prev) => !prev);
    } else {
      setSidebarCollapsed((prev) => !prev);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-background overflow-hidden">
      {/* Banner de Personificação Administrativa (Impersonate) */}
      <ImpersonationBanner
        doctorName={profile?.displayName || user?.name}
        crm={profile?.crm}
        crmState={profile?.crmState}
      />

      {/* Banner Global do Motor de Vendas */}
      <GlobalSalesBanner />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <DoctorSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          mobileOpen={mobileOpen}
          onCloseMobile={() => setMobileOpen(false)}
          isBlocked={isBlocked}
        />
        <div className="flex-1 flex flex-col overflow-hidden">
        {/* Unified Topbar with Plantonista Presence Pill Button */}
        <Topbar onMenuClick={handleToggleSidebar}>
          {isBlocked && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-500/30 shadow-xs">
              <Lock className="w-3.5 h-3.5 text-rose-500" />
              <span>Conta Médica Suspensa / Bloqueada</span>
            </div>
          )}

          {!isApproved && !isBlocked && (
            <button
              type="button"
              onClick={() => setIsOnboardingOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30 hover:bg-amber-500/20 transition-all cursor-pointer shadow-xs animate-pulse"
              title="Cadastro em análise - Clique para completar as etapas"
            >
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
              <span className="hidden sm:inline">Cadastro em Análise (Enviar Docs)</span>
              <span className="sm:hidden">Em Análise</span>
            </button>
          )}

          <button
            onClick={handleToggleOnline}
            disabled={isTogglingStatus || isBlocked}
            className={cn(
              'flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all border shadow-xs cursor-pointer',
              isBlocked
                ? 'bg-rose-50/50 text-rose-600 border-rose-200 cursor-not-allowed opacity-80'
                : !isApproved
                ? 'bg-muted/70 text-muted-foreground border-border hover:bg-muted opacity-80'
                : isOnline
                ? 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50'
                : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
            )}
            title={
              isBlocked
                ? 'Acesso suspenso pelo administrador'
                : !isApproved
                ? 'Cadastro em análise - Complete os documentos para liberar o plantão'
                : isOnline
                ? 'Clique para ficar offline'
                : 'Clique para entrar no plantão'
            }
          >
            {isTogglingStatus ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : isBlocked || !isApproved ? (
              <Lock className="w-3.5 h-3.5 text-muted-foreground" />
            ) : (
              <Radio
                className={cn(
                  'w-3.5 h-3.5',
                  isOnline ? 'text-emerald-500 animate-pulse' : 'text-muted-foreground'
                )}
              />
            )}
            <span className="hidden sm:inline">
              {isBlocked ? 'Acesso Bloqueado' : !isApproved ? 'Plantão Indisponível' : isOnline ? 'Plantonista Online' : 'Plantonista Offline'}
            </span>
            <span className="sm:hidden">
              {isBlocked ? 'Bloqueado' : !isApproved ? 'Indisponível' : isOnline ? 'Online' : 'Offline'}
            </span>
          </button>
        </Topbar>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          {!isApproved ? (
            <div className="max-w-3xl mx-auto py-6 sm:py-10 space-y-6">
              {/* Card Central de Cadastro em Análise */}
              <div className="bg-card border border-border rounded-3xl p-6 sm:p-8 shadow-xl text-center space-y-6 relative overflow-hidden">
                <div className="absolute top-0 inset-x-0 h-2 bg-gradient-to-r from-amber-500 via-primary to-indigo-600" />

                <div
                  className={cn(
                    'w-20 h-20 rounded-3xl mx-auto flex items-center justify-center shadow-sm',
                    isRejected
                      ? 'bg-rose-500/10 text-rose-600 border border-rose-500/20'
                      : 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
                  )}
                >
                  {isRejected ? (
                    <Ban className="w-10 h-10" />
                  ) : (
                    <Clock className="w-10 h-10 animate-pulse" />
                  )}
                </div>

                <div className="max-w-lg mx-auto space-y-2">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border">
                    {isRejected ? (
                      <span className="text-rose-600 bg-rose-50 dark:bg-rose-950/40 border-rose-200">
                        Pendência de Homologação
                      </span>
                    ) : (
                      <span className="text-amber-600 bg-amber-50 dark:bg-amber-950/40 border-amber-200">
                        Aguardando Auditoria Médica
                      </span>
                    )}
                  </div>

                  <h2 className="text-2xl sm:text-3xl font-black text-foreground tracking-tight">
                    {isRejected
                      ? 'Ajustes Solicitados no Cadastro Médico'
                      : 'Cadastro em análise, aguarde aprovação da Medikei'}
                  </h2>

                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {isRejected
                      ? 'O administrador da Medikei analisou sua documentação e apontou pendências que precisam ser corrigidas para liberar seus atendimentos.'
                      : 'Seus dados profissionais, CRM e documentos foram recebidos e estão sendo auditados pela nossa equipe regulatória para garantir o alto padrão de atendimento da plataforma.'}
                  </p>
                </div>

                {/* Se rejeitado, exibir a justificativa detalhada deixada pelo Admin */}
                {isRejected && (
                  <div className="max-w-lg mx-auto p-4 rounded-2xl bg-destructive/10 border border-destructive/20 text-left text-xs text-destructive space-y-1 shadow-xs">
                    <span className="font-bold block uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Motivo informado pelo Administrador:
                    </span>
                    <p className="text-sm font-semibold leading-relaxed">
                      {blockedReason ||
                        'Fotos ou documentos ilegíveis ou incompletos. Por favor, reenvie os arquivos solicitados.'}
                    </p>
                  </div>
                )}

                {/* Resumo da Ficha do Médico */}
                <div className="max-w-lg mx-auto p-4 sm:p-5 rounded-2xl bg-muted/40 border border-border text-left space-y-3 text-xs">
                  <div className="flex items-center justify-between border-b border-border/60 pb-2">
                    <span className="text-muted-foreground font-medium">Médico:</span>
                    <strong className="text-foreground text-sm font-bold">
                      {profile?.displayName || user?.name || 'Dr(a). Médico'}
                    </strong>
                  </div>
                  <div className="flex items-center justify-between border-b border-border/60 pb-2">
                    <span className="text-muted-foreground font-medium">Registro Profissional:</span>
                    <strong className="text-foreground font-mono font-bold">
                      CRM {profile?.crm || '--'}/{profile?.crmState || 'SP'}
                    </strong>
                  </div>
                  <div className="flex items-center justify-between border-b border-border/60 pb-2">
                    <span className="text-muted-foreground font-medium">Validação CFM:</span>
                    <span className="inline-flex items-center gap-1 text-emerald-600 font-bold">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Regular no Conselho
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-muted-foreground font-medium">Status do Plantão:</span>
                    <span className="text-amber-600 font-semibold">
                      Bloqueado até homologação da Medikei
                    </span>
                  </div>
                </div>

                {/* Botões de Ação na Tela em Análise */}
                <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsPreviewOpen(true)}
                    className="rounded-xl text-xs font-bold gap-1.5 border-primary/30 text-primary hover:bg-primary/5 cursor-pointer shadow-xs"
                  >
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span>Pré-visualizar Perfil Público</span>
                  </Button>

                  <Button
                    type="button"
                    onClick={() => {
                      setOnboardingInitialStep(1);
                      setIsOnboardingOpen(true);
                    }}
                    className={cn(
                      'rounded-xl text-xs font-bold gap-1.5 shadow-md cursor-pointer',
                      isRejected
                        ? 'bg-rose-600 hover:bg-rose-700 text-white'
                        : 'bg-primary text-primary-foreground'
                    )}
                  >
                    {isRejected ? (
                      <>
                        <Ban className="w-4 h-4" />
                        <span>Ajustar Dados e Reenviar</span>
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4" />
                        <span>Ajustar ou Adicionar Informações</span>
                      </>
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => refetchProfile()}
                    className="rounded-xl text-xs font-semibold gap-1 text-muted-foreground hover:text-foreground"
                    title="Verificar se o admin já aprovou seu cadastro"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    <span>Atualizar Status</span>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      {/* Global Incoming Consultation Modal */}
      {currentInvitation && (
        <IncomingCallModal
          invitation={currentInvitation}
          onClose={() => setCurrentInvitation(null)}
        />
      )}

      {/* Global Doctor Onboarding / Compliance Modal */}
      <DoctorOnboardingModal
        open={isOnboardingOpen}
        onClose={() => setIsOnboardingOpen(false)}
        doctor={profile as any}
        onRefreshProfile={refetchProfile}
        initialStep={onboardingInitialStep}
      />

      {/* Global Doctor Profile Preview Modal */}
      <DoctorProfilePreviewModal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        data={{
          displayName: profile?.displayName || user?.name,
          name: user?.name,
          crm: profile?.crm,
          crmState: profile?.crmState,
          tagline: profile?.tagline,
          bio: profile?.bio,
          photoUrl: profile?.photoUrl,
          coverUrl: profile?.coverUrl,
          specialty: (profile as any)?.specialty || 'Clínica Geral',
          specialties: (profile as any)?.specialties || [],
          languages: profile?.languages || ['Português'],
          awards: profile?.awards || [],
          publications: profile?.publications,
          minAge: profile?.minAge,
          maxAge: profile?.maxAge,
          allowedAgeGroups: profile?.allowedAgeGroups,
          hasTelemedicine: profile?.hasTelemedicine,
          hasInPerson: profile?.hasInPerson,
          hasHomeCare: profile?.hasHomeCare,
          clinicName: profile?.clinicName,
          clinicAddress: profile?.clinicAddress,
          clinicCity: profile?.clinicCity,
          clinicState: profile?.clinicState,
          homeCareRadiusKm: profile?.homeCareRadiusKm,
        }}
      />

      {/* Copiloto IA do Médico com automação in-page */}
      <FloatingHelpButton />
      </div>
    </div>
  );
}
